import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Button, Box, Card, CardContent, Stack, Chip, Checkbox, Tooltip, IconButton, TableSortLabel,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { getBlock } from '@atlas/blocks';
import {
  makeApi, loginRedirect, logoutRedirect, ADMIN_ROLES, LEADS_ROLES, REVIEW_ROLES, EDIT_ROLES, PUBLISH_ROLES, DOC_EDIT_ROLES, DOC_PUBLISH_ROLES, CONTENT_TYPES,
  type Api, type TenantView, type PageSummary, type PageForEdit, type EntrySummary,
  type SessionUser, type ContentType,
} from './api';
import { starterProps } from './starter';
import { Editor } from './Editor';
import { EntryEditor } from './EntryEditor';
import { Team } from './Team';
import { Leads } from './Leads';
import { TenantSetup } from './TenantSetup';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Reviews } from './Reviews';
import { ApiTokens } from './ApiTokens';
import { DocReviews } from './DocReviews';
import { ChromeEditor } from './ChromeEditor';
import { DocsChromeEditor } from './DocsChromeEditor';
import WebAssetOutlinedIcon from '@mui/icons-material/WebAssetOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Layout, type NavGroup, type NavItem } from './Layout';
import { MediaLibrary, MediaPickerProvider } from './MediaPicker';
import { DocsManager } from './DocsManager';
import { ThemeCustomizerProvider } from './ThemeCustomizer';
import { Pager } from './Pager';
import { ConfirmDialog } from './dialogs';

// Humanise a slug into a display name, e.g. "about-us" → "About Us".
const humanizeSlug = (s: string) => s.replace(/[-_/]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || s;

const NAV_ICON: Record<string, React.ReactNode> = {
  pages: <DescriptionOutlinedIcon fontSize="small" />,
  templates: <ViewQuiltOutlinedIcon fontSize="small" />,
  insight: <LightbulbOutlinedIcon fontSize="small" />,
  new: <NewspaperOutlinedIcon fontSize="small" />,
  'solution-brief': <SummarizeOutlinedIcon fontSize="small" />,
  reviews: <RateReviewOutlinedIcon fontSize="small" />,
  team: <PeopleOutlinedIcon fontSize="small" />,
  leads: <InboxOutlinedIcon fontSize="small" />,
  'tenant-setup': <TuneOutlinedIcon fontSize="small" />,
  media: <PermMediaOutlinedIcon fontSize="small" />,
  docs: <MenuBookOutlinedIcon fontSize="small" />,
  'doc-reviews': <FactCheckOutlinedIcon fontSize="small" />,
  'api-tokens': <VpnKeyOutlinedIcon fontSize="small" />,
};

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>{children}</Box>;
}

function Login({ onLogin }: { onLogin: () => void }) {
  return (
    <Centered>
      <Card sx={{ maxWidth: 380, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.main', display: 'grid', placeItems: 'center', mx: 'auto', mb: 2, color: '#fff', fontWeight: 800, fontSize: 22 }}>A</Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Atlas CMS</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Sign in to manage your website content.</Typography>
          <Button variant="contained" size="large" fullWidth onClick={onLogin}>Log in</Button>
        </CardContent>
      </Card>
    </Centered>
  );
}

const publicBase = (collection?: string) =>
  collection === 'insight' ? 'insights' : collection === 'new' ? 'news' : collection ?? '';

function StatusChips({ status, reviewStatus, hasUnpublished }: { status: string; reviewStatus?: string; hasUnpublished?: boolean }) {
  return (
    <>
      <Chip size="small" label={status === 'PUBLISHED' ? 'Published' : status === 'DRAFT' ? 'Draft' : status} color={status === 'PUBLISHED' ? 'success' : 'default'} />
      {reviewStatus === 'IN_REVIEW' ? <Chip size="small" label="In review" color="warning" sx={{ ml: 1 }} />
        : hasUnpublished ? <Chip size="small" label="Draft ahead" color="warning" variant="outlined" sx={{ ml: 1 }} /> : null}
    </>
  );
}

function EmptyState({ icon, label, hint }: { icon: React.ReactNode; label: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
      <Box sx={{ fontSize: 44, opacity: 0.35, mb: 1 }}>{icon}</Box>
      <Typography>{label}</Typography>
      {hint ? <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>{hint}</Typography> : null}
    </Paper>
  );
}

/** The list view for one content type — pages/templates (block-based) or an
 *  entry collection. Handles create + opening the right editor. */
function ContentList({
  api, tenant, type, canEdit, canDelete, onOpenPage, onOpenEntry,
}: {
  api: Api; tenant: TenantView; type: ContentType; canEdit: boolean; canDelete: boolean;
  onOpenPage: (p: PageForEdit) => void;
  onOpenEntry: (type: ContentType, id: string | null) => void;
}) {
  const isPage = type.kind === 'page';
  const [pages, setPages] = useState<PageSummary[] | null>(null);
  const [entries, setEntries] = useState<EntrySummary[] | null>(null);
  const [err, setErr] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // sort / paginate / select
  const [sortAsc, setSortAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | { ids: string[]; label: string }>(null);

  const load = () => {
    setErr(''); setPages(null); setEntries(null); setSelected(new Set());
    if (isPage) api.pages(tenant.tenantId, type.pageKind).then(setPages).catch((e) => setErr(e.message));
    else api.entries(tenant.tenantId, type.collection!).then(setEntries).catch((e) => setErr(e.message));
  };
  useEffect(load, [tenant.tenantId, type.key]);
  useEffect(() => { setPage(1); setSelected(new Set()); }, [type.key, sortAsc, pageSize]);

  const openPage = async (id: string) => {
    setOpening(id);
    try { onOpenPage(await api.page(tenant.tenantId, id)); }
    catch (e: any) { setErr(e.message); }
    finally { setOpening(null); }
  };

  const createPage = async () => {
    setCreating(true);
    try {
      const { id } = await api.createPage(tenant.tenantId, slug.trim());
      if (type.pageKind === 'TEMPLATE') {
        try {
          const def = getBlock('templateHero');
          if (def) await api.saveDraft(tenant.tenantId, id, [{ type: 'templateHero', props: starterProps(def.schema) }], null);
        } catch { /* seeding is best-effort */ }
      }
      setCreateOpen(false); setSlug('');
      await openPage(id);
    } catch (e: any) {
      setErr(e.message === 'slug_taken' ? 'That URL is already used.' : `Couldn’t create: ${e.message}`);
    } finally { setCreating(false); }
  };

  const duplicate = async (id: string) => {
    setBusyId(id);
    try { await api.duplicatePage(tenant.tenantId, id); load(); }
    catch (e: any) { setErr(e.message); } finally { setBusyId(null); }
  };
  const doDelete = async (ids: string[]) => {
    setConfirm(null);
    try {
      for (const id of ids) {
        if (isPage) await api.deletePage(tenant.tenantId, id);
        else await api.deleteEntry(tenant.tenantId, id);
      }
      load();
    } catch (e: any) { setErr(e.message); }
  };

  const onNew = () => { if (isPage) setCreateOpen(true); else onOpenEntry(type, null); };

  const loading = isPage ? pages === null : entries === null;

  // Build a unified, sorted, paginated row set for either content kind.
  type Row = { id: string; name: string; url: string; seo: string | null; status: string; reviewStatus?: string; hasUnpublished?: boolean; updated: string };
  const allRows: Row[] = isPage
    ? (pages ?? []).map((p) => ({ id: p.id, name: humanizeSlug(p.slug), url: `/${p.slug}`, seo: p.seoTitle, status: p.status, reviewStatus: p.reviewStatus, hasUnpublished: p.hasUnpublished, updated: p.updatedAt }))
    : (entries ?? []).map((e) => ({ id: e.id, name: e.title, url: `/${publicBase(e.collection)}/${e.slug}`, seo: null, status: e.status, updated: e.publishedAt ?? '' }));
  const sorted = [...allRows].sort((a, b) => (sortAsc ? 1 : -1) * a.name.localeCompare(b.name));
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);
  const pageIds = rows.map((r) => r.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); allOnPageSelected ? pageIds.forEach((id) => n.delete(id)) : pageIds.forEach((id) => n.add(id)); return n; });
  const toggleOne = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <Box sx={{ maxWidth: 1080 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Loading…' : `${allRows.length} ${allRows.length === 1 ? type.singular : type.label.toLowerCase()}`}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {canDelete && selected.size > 0 ? (
            <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirm({ ids: [...selected], label: `${selected.size} selected` })}>
              Delete ({selected.size})
            </Button>
          ) : null}
          {canEdit ? <Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>New {type.singular}</Button> : null}
        </Stack>
      </Stack>

      {err ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
      ) : allRows.length === 0 ? (
        <EmptyState icon={NAV_ICON[type.key]} label={`No ${type.label.toLowerCase()} yet.`} hint={canEdit ? `Use “New ${type.singular}” to add one.` : undefined} />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canDelete ? (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allOnPageSelected} indeterminate={!allOnPageSelected && pageIds.some((id) => selected.has(id))} onChange={toggleAll} />
                    </TableCell>
                  ) : null}
                  <TableCell sortDirection={sortAsc ? 'asc' : 'desc'}>
                    <TableSortLabel active direction={sortAsc ? 'asc' : 'desc'} onClick={() => setSortAsc((v) => !v)}>Name</TableSortLabel>
                  </TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>SEO</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover selected={selected.has(r.id)}>
                    {canDelete ? (
                      <TableCell padding="checkbox"><Checkbox size="small" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} /></TableCell>
                    ) : null}
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, color: 'text.secondary' }}>{r.url}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: 13, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.seo || '—'}</TableCell>
                    <TableCell><StatusChips status={r.status} reviewStatus={r.reviewStatus} hasUnpublished={r.hasUnpublished} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <span><IconButton size="small" disabled={opening === r.id || !!busyId} onClick={() => (isPage ? openPage(r.id) : onOpenEntry(type, r.id))}><EditIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                      {isPage && canEdit ? (
                        <Tooltip title="Duplicate">
                          <span><IconButton size="small" disabled={!!busyId} onClick={() => duplicate(r.id)}><ContentCopyOutlinedIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                      ) : null}
                      {canDelete ? (
                        <Tooltip title="Delete">
                          <span><IconButton size="small" color="error" disabled={!!busyId} onClick={() => setConfirm({ ids: [r.id], label: `“${r.name}”` })}><DeleteOutlineIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pager page={page} pageCount={pageCount} pageSize={pageSize} total={allRows.length} onPage={setPage} onPageSize={setPageSize} />
        </>
      )}

      <ConfirmDialog
        open={!!confirm} title={`Delete ${type.singular}`}
        message={`Delete ${confirm?.label ?? ''}? This can’t be undone.`}
        confirmLabel="Delete" danger
        onClose={(ok) => (ok && confirm ? doDelete(confirm.ids) : setConfirm(null))}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>New {type.singular}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Page URL (slug)" fullWidth value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            helperText="Lowercase words separated by hyphens, e.g. about-us"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createPage} disabled={!slug || creating}>{creating ? 'Creating…' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Shell() {
  const api = useMemo<Api>(() => makeApi(), []);
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);
  const [tenants, setTenants] = useState<TenantView[] | null>(null);
  const [tenantId, setTenantId] = useState<string>('');
  const [nav, setNav] = useState<string>('pages');
  const [editingPage, setEditingPage] = useState<PageForEdit | null>(null);
  const [editingEntry, setEditingEntry] = useState<{ type: ContentType; id: string | null } | null>(null);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [docReviewCount, setDocReviewCount] = useState<number>(0);
  const [docJump, setDocJump] = useState<{ pageId: string; spaceId: string } | null>(null); // page to open in DocsManager
  const [err, setErr] = useState('');

  useEffect(() => { api.me().then(setUser).catch(() => setUser(null)); }, [api]);
  useEffect(() => {
    if (!user) return;
    api.myTenants().then((ts) => { setTenants(ts); if (ts[0]) setTenantId(ts[0].tenantId); }).catch((e) => setErr(e.message));
  }, [user, api]);

  const tenant = tenants?.find((t) => t.tenantId === tenantId) ?? tenants?.[0];
  const canReview = tenant ? REVIEW_ROLES.includes(tenant.role) : false;

  // Review-queue badge (best-effort).
  useEffect(() => {
    if (!tenant || !canReview) { setReviewCount(0); return; }
    api.reviewQueue(tenant.tenantId).then((q) => setReviewCount(q.length)).catch(() => setReviewCount(0));
  }, [tenant?.tenantId, canReview, editingPage]);

  // Docs review-queue badge (best-effort). Refreshes when leaving a doc review.
  useEffect(() => {
    const can = tenant ? DOC_EDIT_ROLES.includes(tenant.role) : false;
    if (!tenant || !can) { setDocReviewCount(0); return; }
    api.docReviewQueue(tenant.tenantId).then((q) => setDocReviewCount(q.length)).catch(() => setDocReviewCount(0));
  }, [tenant?.tenantId, tenant?.role, nav, docJump]);

  if (user === undefined) return <Centered><CircularProgress /></Centered>;
  if (!user) return <Login onLogin={loginRedirect} />;
  if (err) return <Centered><Alert severity="error">{err}</Alert></Centered>;
  if (!tenants) return <Centered><CircularProgress /></Centered>;
  if (!tenants.length) return <Centered><Alert severity="info">Your account has no product access yet. Ask an admin to add you.</Alert></Centered>;
  if (!tenant) return <Centered><CircularProgress /></Centered>;

  // Website and docs are gated independently — a role may edit one surface, both,
  // or neither. Do NOT derive docs perms from website perms (that leaks a
  // website-only role into docs and vice-versa).
  const canEdit = EDIT_ROLES.includes(tenant.role);
  const canDelete = PUBLISH_ROLES.includes(tenant.role);
  const canDocsEdit = DOC_EDIT_ROLES.includes(tenant.role);
  const canDocsPublish = DOC_PUBLISH_ROLES.includes(tenant.role);
  const isLeads = LEADS_ROLES.includes(tenant.role);
  const isAdmin = ADMIN_ROLES.includes(tenant.role);
  const isSuperAdmin = tenant.role === 'SUPER_ADMIN';

  // Focused, full-screen editors — still wrapped in the media picker provider so
  // image fields can browse/upload from the library.
  let inner: React.ReactNode;
  if (editingPage) {
    inner = <Editor api={api} tenant={tenant} page={editingPage} onBack={() => setEditingPage(null)} />;
  } else if (editingEntry) {
    inner = (
      <EntryEditor
        api={api} tenant={tenant} type={editingEntry.type} id={editingEntry.id}
        canDelete={isAdmin || tenant.role === 'EDITOR'}
        onBack={() => setEditingEntry(null)}
      />
    );
  } else {
    // Show only the surfaces this role can author; scoped roles (WEB_EDITOR /
    // DOCS_EDITOR) see just their surface. Admins/generic roles see both.
    const groups: NavGroup[] = [];
    if (canEdit) {
      // Marketing content — its Header & Footer lives here, with the site it configures.
      const contentItems: NavItem[] = CONTENT_TYPES.map((c) => ({ key: c.key, label: c.label, icon: NAV_ICON[c.key] }));
      if (isAdmin) contentItems.push({ key: 'chrome', label: 'Header & Footer', icon: <WebAssetOutlinedIcon fontSize="small" /> });
      groups.push({ heading: 'Content', items: contentItems });
    }
    if (canDocsEdit) {
      // Documentation — its own Header & Footer (docs-chrome), separate from marketing.
      const docItems: NavItem[] = [
        { key: 'docs', label: 'Docs', icon: NAV_ICON.docs },
        { key: 'doc-reviews', label: 'Doc Reviews', icon: NAV_ICON['doc-reviews'], badge: docReviewCount },
      ];
      if (isAdmin) docItems.push({ key: 'docs-chrome', label: 'Header & Footer', icon: <WebAssetOutlinedIcon fontSize="small" /> });
      groups.push({ heading: 'Documentation', items: docItems });
    }
    groups.push({ heading: 'Library', items: [{ key: 'media', label: 'Media', icon: NAV_ICON.media }] });
    const manage = [];
    if (canReview) manage.push({ key: 'reviews', label: 'Reviews', icon: NAV_ICON.reviews, badge: reviewCount });
    if (isLeads) manage.push({ key: 'leads', label: 'Leads', icon: NAV_ICON.leads });
    if (isAdmin) manage.push({ key: 'team', label: 'Team', icon: NAV_ICON.team });
    if (isAdmin) manage.push({ key: 'api-tokens', label: 'API Tokens', icon: NAV_ICON['api-tokens'] });
    if (isSuperAdmin) manage.push({ key: 'tenant-setup', label: 'Tenant Setup', icon: NAV_ICON['tenant-setup'] });
    if (manage.length) groups.push({ heading: 'Manage', items: manage });

    // Default nav is 'pages'; redirect to an allowed surface if this role can't
    // reach the current one (e.g. a docs-only user landing on Content).
    let effectiveNav = nav;
    if (CONTENT_TYPES.some((c) => c.key === effectiveNav) && !canEdit) effectiveNav = canDocsEdit ? 'docs' : isLeads ? 'leads' : 'media';
    else if (effectiveNav === 'docs' && !canDocsEdit) effectiveNav = canEdit ? 'pages' : isLeads ? 'leads' : 'media';
    // A leads-only role (Business Member) has no content/docs/media surfaces — land on Leads.
    if (isLeads && !canEdit && !canDocsEdit && !isAdmin && !canReview) effectiveNav = 'leads';

    const activeType = CONTENT_TYPES.find((c) => c.key === effectiveNav);
    const title = effectiveNav === 'reviews' ? 'Review queue' : effectiveNav === 'leads' ? 'Leads' : effectiveNav === 'tenant-setup' ? 'Tenant Setup' : effectiveNav === 'team' ? 'Team' : effectiveNav === 'api-tokens' ? 'API Tokens' : effectiveNav === 'chrome' ? 'Header & Footer' : effectiveNav === 'docs-chrome' ? 'Docs header & footer' : effectiveNav === 'doc-reviews' ? 'Doc reviews' : effectiveNav === 'media' ? 'Media library' : effectiveNav === 'docs' ? 'Documentation' : activeType?.label ?? 'Content';

    let content: React.ReactNode;
    if (effectiveNav === 'reviews') content = <Reviews api={api} tenant={tenant} onOpen={(p) => setEditingPage(p)} />;
    else if (effectiveNav === 'leads') content = <Leads api={api} tenant={tenant} />;
    else if (effectiveNav === 'tenant-setup') content = <TenantSetup api={api} tenant={tenant} />;
    else if (effectiveNav === 'team') content = <Team api={api} tenant={tenant} />;
    else if (effectiveNav === 'api-tokens') content = <ApiTokens api={api} tenant={tenant} />;
    else if (effectiveNav === 'chrome') content = <ChromeEditor api={api} tenant={tenant} />;
    else if (effectiveNav === 'docs-chrome') content = <DocsChromeEditor api={api} tenant={tenant} canEdit={canDocsPublish} />;
    else if (effectiveNav === 'doc-reviews') content = <DocReviews api={api} tenant={tenant} onOpen={(pageId, spaceId) => { setDocJump({ pageId, spaceId }); setNav('docs'); }} />;
    else if (effectiveNav === 'media') content = <MediaLibrary api={api} tenantId={tenant.tenantId} canUpload={canEdit || canDocsEdit} canDelete={canDelete || canDocsPublish} />;
    else if (effectiveNav === 'docs') content = <DocsManager api={api} tenant={tenant} canEdit={canDocsEdit} canPublish={canDocsPublish} jumpTo={docJump} onJumped={() => setDocJump(null)} />;
    else if (activeType) {
      content = (
        <ContentList
          api={api} tenant={tenant} type={activeType} canEdit={canEdit} canDelete={canDelete}
          onOpenPage={(p) => setEditingPage(p)}
          onOpenEntry={(type, id) => setEditingEntry({ type, id })}
        />
      );
    }

    inner = (
      <Layout
        groups={groups} activeKey={effectiveNav} onNav={setNav} title={title}
        tenants={tenants} tenant={tenant} email={user.email}
        onSwitchTenant={(id) => { setTenantId(id); setNav('pages'); }}
        onLogout={logoutRedirect}
      >
        {content}
      </Layout>
    );
  }

  return (
    <MediaPickerProvider api={api} tenantId={tenant.tenantId} canUpload={canEdit} canDelete={canDelete}>
      {inner}
    </MediaPickerProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <ThemeCustomizerProvider>
    <Shell />
  </ThemeCustomizerProvider>,
);
